<template>
  <div class="pedit" dir="rtl" v-if="page_loaded" :key="profile.id">

    <!-- success banner -->
    <div v-if="savedMsg" class="pedit__banner pedit__banner--success" role="status" aria-live="polite">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
      <span>{{ savedMsg }}</span>
    </div>

    <div class="pedit__page">

      <!-- step 1: welcome -->
      <div class="pedit__card" v-if="step === 1">
        <h2 class="pedit__heading">הגדרת פרופיל</h2>
        <p class="pedit__body">
          ברוך הבא לעולם התוכן האיכותי והמשפחתי של TOV!<br>
          אנחנו עובדים כל העת כדי להביא תכנים נקיים, מותאמים וברמה גבוהה.<br>
          העדפות הסינון משתנות מאדם לאדם — הגדר בעצמך את התוכן שתרצה לצפות בו.
        </p>
        <ui-button variant="primary" size="lg" @click="step = 2">המשך</ui-button>
      </div>

      <!-- step 2: preferences -->
      <div class="pedit__card" v-if="step === 2">
        <h2 class="pedit__heading">העדפות צפייה</h2>

        <div class="pedit__field-group">
          <p class="pedit__question">האם תרצה לקבל התראות לפני רגעים בעייתיים בסרטים?</p>
          <div class="pedit__radio-row">
            <label class="pedit__radio-label">
              <input type="radio" v-model="show_alert" :value="true" class="pedit__radio-native" />
              <span class="pedit__radio-pill" :class="{ 'pedit__radio-pill--active': show_alert === true }">כן</span>
            </label>
            <label class="pedit__radio-label">
              <input type="radio" v-model="show_alert" :value="false" class="pedit__radio-native" />
              <span class="pedit__radio-pill" :class="{ 'pedit__radio-pill--active': show_alert === false }">לא</span>
            </label>
          </div>
        </div>

        <div class="pedit__field-group">
          <p class="pedit__question">האם תעדיף שהמערכת תדלג אוטומטית על תכנים בעייתיים?</p>
          <div class="pedit__radio-row">
            <label class="pedit__radio-label">
              <input type="radio" v-model="show_skip" :value="true" class="pedit__radio-native" />
              <span class="pedit__radio-pill" :class="{ 'pedit__radio-pill--active': show_skip === true }">כן</span>
            </label>
            <label class="pedit__radio-label">
              <input type="radio" v-model="show_skip" :value="false" class="pedit__radio-native" />
              <span class="pedit__radio-pill" :class="{ 'pedit__radio-pill--active': show_skip === false }">לא</span>
            </label>
          </div>
        </div>

        <div class="pedit__field-group">
          <p class="pedit__question">האם למשתמש תהיה הרשאה לערוך את הפרופיל?</p>
          <div class="pedit__radio-row">
            <label class="pedit__radio-label">
              <input type="radio" v-model="has_perms" :value="true" class="pedit__radio-native" />
              <span class="pedit__radio-pill" :class="{ 'pedit__radio-pill--active': has_perms === true }">כן</span>
            </label>
            <label class="pedit__radio-label">
              <input type="radio" v-model="has_perms" :value="false" class="pedit__radio-native" />
              <span class="pedit__radio-pill" :class="{ 'pedit__radio-pill--active': has_perms === false }">לא</span>
            </label>
          </div>
        </div>

        <div class="pedit__actions">
          <ui-button variant="ghost" size="md" @click="step = 1">חזרה</ui-button>
          <ui-button variant="primary" size="lg" @click="step = 3">המשך</ui-button>
        </div>
      </div>

      <!-- step 3: name + PIN -->
      <div class="pedit__card" v-if="step === 3">
        <h2 class="pedit__heading">שם ו-PIN</h2>

        <ui-input
          label="שם פרופיל"
          v-model="profile.name"
          placeholder="הקלד שם"
          :required="true"
          class="pedit__input"
        />

        <div class="pedit__field-group">
          <span class="pedit__pin-label">PIN (4 ספרות, אופציונלי)</span>
          <div class="pedit__pin-row" role="group" aria-label="ספרות PIN">
            <div
              v-for="(digit, idx) in pinDigits"
              :key="'pin' + idx"
              class="pedit__pin-cell"
              :class="{ 'pedit__pin-cell--filled': digit !== '' }"
              aria-hidden="true"
            >{{ digit !== '' ? '•' : '' }}</div>
          </div>
          <!-- hidden real input that collects PIN -->
          <input
            type="password"
            maxlength="4"
            inputmode="numeric"
            pattern="[0-9]*"
            v-model="profile.password"
            class="pedit__pin-hidden"
            placeholder="הקלד 4 ספרות"
            autocomplete="new-password"
            aria-label="קוד PIN בן 4 ספרות"
            @input="syncPinDigits"
          />
          <p class="pedit__pin-hint">השאר ריק אם אינך רוצה נעילת PIN</p>
        </div>

        <div class="pedit__actions">
          <ui-button variant="ghost" size="md" @click="step = 2">חזרה</ui-button>
          <ui-button variant="primary" size="lg" @click="step = 4">המשך</ui-button>
        </div>
      </div>

      <!-- step 4: content restrictions -->
      <div class="pedit__card" v-if="step === 4">
        <h2 class="pedit__heading">רמת סינון תוכן</h2>
        <p class="pedit__body">בחר את רמת ההגנה והסינון שמתאימה לך.</p>

        <div class="pedit__restrictions-grid">
          <label
            v-for="item in restrictions_list"
            :key="item.id"
            class="pedit__rest-card"
            :class="{ 'pedit__rest-card--active': plan_id === item.id }"
          >
            <input type="radio" :value="item.id" v-model="plan_id" class="pedit__radio-native" />
            <!-- original icon class replaced with neutral SVG per UX audit -->
            <!-- <i :class="item.icon_url" class="pedit__rest-icon" aria-hidden="true"></i> -->
            <svg class="pedit__rest-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>
            <span class="pedit__rest-name">{{ item.name }}</span>
            <span class="pedit__rest-desc">{{ item.details }}</span>
          </label>
        </div>

        <div class="pedit__actions">
          <ui-button variant="ghost" size="md" @click="step = 3">חזרה</ui-button>
          <ui-button variant="primary" size="lg" :loading="saving" @click="save">שמור פרופיל</ui-button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import UiButton from './ui/Button.vue';
import UiInput  from './ui/Input.vue';

export default {
  components: { UiButton, UiInput },
  data() {
    return {
      page_loaded: false,
      step: 1,
      id: this.$route.params.id,
      profile: false,
      restrictions: [],
      saved_restrictions: [],
      insert_mode: false,
      show_alert: true,
      has_perms: true,
      show_skip: true,
      plan_id: 3,
      saving: false,
      savedMsg: '',
      pinDigits: ['', '', '', ''],
    };
  },
  computed: {
    restrictions_list() {
      var restrictions = JSON.parse(JSON.stringify(this.$store.state.restrictions_plans));
      for (let i in restrictions) {
        restrictions[i].is_active = (this.saved_restrictions.indexOf(restrictions[i].id) !== -1);
      }
      return restrictions;
    },
  },
  methods: {
    syncPinDigits() {
      var val = (this.profile.password || '').split('');
      this.pinDigits = [
        val[0] || '',
        val[1] || '',
        val[2] || '',
        val[3] || '',
      ];
    },
    save() {
      this.saving = true;
      this.api_call({
        url: 'save_profile',
        data: {
          insert_mode: true,
          id: this.id,
          name: this.profile.name,
          age: this.profile.age,
          plan_id: this.plan_id,
          limit_hours: this.profile.limit_hours,
          password: this.profile.password,
          perms_show_alerts: this.show_alert,
          perms_auto_skip: this.show_skip,
          perms_block_items: (this.profile.perms_block_items ? 1 : 0),
          has_perms: this.has_perms,
          new_setup: (this.profile.new_setup ? 0 : 1),
        },
        onComplete: () => {
          this.saving = false;
          document.location = '/';
        },
      });
    },
  },
  mounted() {
    if (this.id > 0) {
      this.api_call({
        url: 'get_profile',
        data: { id: this.id },
        onComplete: (data) => {
          data.data.data.perms_show_alerts = (data.data.data.perms_show_alerts == 1);
          data.data.data.perms_auto_skip   = (data.data.data.perms_auto_skip == 1);
          data.data.data.perms_block_items = (data.data.data.perms_block_items == 1);
          this.profile = data.data.data;
          this.page_loaded = true;
          this.saved_restrictions = data.data.data.restrictions;
          this.syncPinDigits();
        },
      });
    } else {
      this.insert_mode = true;
      this.page_loaded = true;
      this.profile = { name: '', age: '', restrictions: [], limit_hours: '', password: '' };
    }
  },
};
</script>

<style scoped lang="scss">
.pedit {
  min-height: 100vh;
  background: var(--color-bg);
  font-family: var(--font-hebrew);
  padding-block: var(--space-12);
}

.pedit__page {
  max-width: 560px;
  margin-inline: auto;
  padding-inline: var(--space-6);
}

.pedit__card {
  background: var(--color-surface1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.pedit__heading {
  font-size: 22px;
  font-weight: 600;
  color: var(--color-text);
  letter-spacing: -0.2px;
  margin: 0;
}

.pedit__body {
  font-size: 15px;
  color: var(--color-textMuted);
  line-height: 1.6;
  margin: 0;
}

/* field group */
.pedit__field-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.pedit__question {
  font-size: 14px;
  color: var(--color-text);
  margin: 0;
}

/* radio pill */
.pedit__radio-row {
  display: flex;
  gap: var(--space-3);
}

.pedit__radio-label {
  display: flex;
  cursor: pointer;
}

.pedit__radio-native {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.pedit__radio-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding-inline: 20px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-textMuted);
  font-size: 14px;
  font-weight: 500;
  transition: all var(--duration-fast) var(--easing-default);
}

.pedit__radio-pill--active {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}

.pedit__radio-label:focus-within .pedit__radio-pill {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* PIN */
.pedit__pin-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-textMuted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.pedit__pin-row {
  display: flex;
  gap: var(--space-3);
}

.pedit__pin-cell {
  width: 56px;
  height: 64px;
  border-radius: var(--radius-sm);
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  font-family: var(--font-mono);
  transition: border-color var(--duration-fast) var(--easing-default);
}

.pedit__pin-cell--filled {
  border-color: var(--color-text);
}

.pedit__pin-hidden {
  height: 48px;
  width: 100%;
  padding-inline: 14px;
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  font-size: 15px;
  font-family: var(--font-hebrew);
  outline: none;
  transition: border-color var(--duration-fast) var(--easing-default),
              box-shadow var(--duration-fast) var(--easing-default);
}

.pedit__pin-hidden:focus {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(225, 29, 42, 0.2);
}

.pedit__pin-hint {
  font-size: 12px;
  color: var(--color-textMuted);
  margin: 0;
}

/* restrictions */
.pedit__restrictions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: var(--space-3);
}

.pedit__rest-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: center;
  transition: border-color var(--duration-base) var(--easing-default),
              background var(--duration-base) var(--easing-default);
}

.pedit__rest-card--active {
  border-color: var(--color-accent);
  background: var(--color-accentSoft);
}

.pedit__rest-card:focus-within {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.pedit__rest-icon {
  font-size: 24px;
  color: var(--color-textMuted);
}

.pedit__rest-card--active .pedit__rest-icon {
  color: var(--color-accent);
}

.pedit__rest-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.pedit__rest-desc {
  font-size: 12px;
  color: var(--color-textMuted);
  line-height: 1.4;
}

/* action row */
.pedit__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  border-block-start: 1px solid var(--color-border);
  padding-block-start: var(--space-4);
}

/* success banner */
.pedit__banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 14px 20px;
  border-radius: var(--radius-md);
  font-size: 14px;
  margin-block-end: var(--space-6);
  max-width: 560px;
  margin-inline: auto;
}

.pedit__banner--success {
  background: rgba(31,174,107,0.12);
  border: 1px solid rgba(31,174,107,0.33);
  color: var(--color-success);
}

.pedit__input {
  /* forward font to embedded input */
  --font-sans: var(--font-hebrew);
}

@media (prefers-reduced-motion: reduce) {
  .pedit__pin-cell,
  .pedit__radio-pill,
  .pedit__rest-card,
  .pedit__pin-hidden {
    transition: none;
  }
}
</style>
